import { useState, useMemo, useCallback } from 'react'
import { ClientSideTable } from 'Presentation/Components/Table'
import clsx from 'clsx'
import { KTSVG } from '_metronic/helpers'
import { toast } from 'react-toastify'
import ActionCell from './ActionCell'
import PreboreSettingModal from './PreboreSettingModal'
import PlanSummaryCards from './components/PlanSummaryCards'
import useViewModel from './ViewModel'
import {
  mapPileHolesToTableRows,
  formatPreboreCell,
  getStatusLabel,
  getStatusBadgeClass,
  PileHoleTableRow,
  PileHoleStatusFilter,
} from './pileHoleMapper'
import { parsePreborePayload } from 'Utils/prebore'

const STATUS_FILTERS: { value: PileHoleStatusFilter; label: string }[] = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'in-progress', label: 'กำลังดำเนินการ' },
  { value: 'completed', label: 'เสร็จสิ้น' },
  { value: 'repairing', label: 'เสียหาย - ซ่อมแซม' },
  { value: 'change-pile', label: 'เสียหาย - เปลี่ยน' },
]

const HoleList: React.FC = () => {
  const { data, projectId, loading, refetch, onContinueDriving, updatePileHolePrebore } =
    useViewModel()
  const [openPreboreModal, setOpenPreboreModal] = useState(false)
  const [selectedHole, setSelectedHole] = useState<PileHoleTableRow | null>(null)
  const [statusFilter, setStatusFilter] = useState<PileHoleStatusFilter>('all')
  const [isSavingPrebore, setIsSavingPrebore] = useState(false)

  const tableRows = useMemo(() => mapPileHolesToTableRows(data), [data])

  const hasData = tableRows.length > 0

  const filteredData = useMemo(() => {
    if (statusFilter === 'all') return tableRows
    return tableRows.filter(row => row.status === statusFilter)
  }, [tableRows, statusFilter])

  const handleOpenPreboreModal = useCallback((row: PileHoleTableRow) => {
    setSelectedHole(row)
    setOpenPreboreModal(true)
  }, [])

  const handleClosePreboreModal = () => {
    setOpenPreboreModal(false)
    setSelectedHole(null)
  }

  const handleSavePrebore = async (
    depth: string,
    drillSize: string,
    pileNo: string,
    gridLine: string,
    location: string
  ) => {
    if (!projectId || !selectedHole) return
    setIsSavingPrebore(true)
    const { success, data: errData } = await updatePileHolePrebore(
      parseInt(projectId),
      selectedHole.id,
      parsePreborePayload(depth, drillSize, pileNo, gridLine, location)
    )
    setIsSavingPrebore(false)

    if (!success) {
      toast.error(errData)
      return
    }

    toast.success('บันทึกตั้งค่า prebore สำเร็จ')
    handleClosePreboreModal()
    refetch()
  }

  const selectedPrebore = selectedHole
    ? {
        preboreDepth: selectedHole.preboreDepth,
        preboreDrillSize: selectedHole.preboreDrillSize,
        pileNo: selectedHole.pileNo,
        gridLine: selectedHole.gridLine,
        location: selectedHole.location,
      }
    : { preboreDepth: null, preboreDrillSize: null, pileNo: null, gridLine: null, location: null }

  const COLUMN_CONFIGS = useMemo(
    () => [
      {
        Header: 'ลำดับหลุม',
        accessor: 'seq',
        width: 100,
        minWidth: 100,
        Cell: ({ value }: any) => <span className='text-gray-800 px-5'>{value}</span>,
      },
      {
        Header: 'สถานะ',
        accessor: 'status',
        width: 140,
        minWidth: 140,
        Cell: ({ value }: any) => (
          <div className={clsx('badge', getStatusBadgeClass(value))}>
            {getStatusLabel(value)}
          </div>
        ),
      },
      {
        Header: 'Prebore',
        accessor: 'preboreDepth',
        width: 220,
        minWidth: 220,
        Cell: ({ row }: any) => (
          <span className='text-gray-800'>
            {formatPreboreCell(row.original.preboreDepth, row.original.preboreDrillSize)}
          </span>
        ),
      },
      {
        Header: 'Pile No.',
        accessor: 'pileNo',
        width: 120,
        minWidth: 120,
        Cell: ({ value }: any) => <span className='text-gray-800'>{value ?? '-'}</span>,
      },
      {
        Header: 'Grid Line',
        accessor: 'gridLine',
        width: 120,
        minWidth: 120,
        Cell: ({ value }: any) => <span className='text-gray-800'>{value ?? '-'}</span>,
      },
      {
        Header: 'ชื่อผังทำงาน',
        accessor: 'location',
        width: 120,
        minWidth: 120,
        Cell: ({ value }: any) => <span className='text-gray-800'>{value ?? '-'}</span>,
      },
      {
        Header: 'รหัสเสา',
        accessor: 'poleCodes',
        width: 200,
        minWidth: 200,
        Cell: ({ value }: any) => {
          if (!value?.length) return <span className='text-gray-500'>-</span>
          return (
            <div className='text-gray-800 d-flex flex-column'>
              {value.map((code: string, i: number) => (
                <span key={i}>{code}</span>
              ))}
            </div>
          )
        },
      },
      {
        Header: '',
        accessor: 'action',
        width: 100,
        minWidth: 100,
        Cell: ({ row }: any) => (
          <div className='d-flex justify-content-end'>
            <ActionCell
              row={row.original}
              onClickPrebore={handleOpenPreboreModal}
              onContinueDriving={onContinueDriving}
            />
          </div>
        ),
        disableSortBy: true,
      },
    ],
    [handleOpenPreboreModal, onContinueDriving]
  )

  return (
    <>
      <div className='card'>
        <div className='card-header'>
          <div className='card-title'>
            {hasData && <span className='text-gray-800 fw-bold'>รายการหลุม</span>}
          </div>
        </div>

        {loading && !hasData ? (
          <div className='card-body py-15 text-center text-gray-600'>กำลังโหลด...</div>
        ) : !hasData ? (
          <div className='card-body py-15'>
            <div className='d-flex flex-column align-items-center text-center'>
              <KTSVG
                path='/media/icons/duotune/general/gen025.svg'
                className='svg-icon-5x svg-icon-muted mb-6'
              />
              <h3 className='text-gray-800 fw-bold mb-3'>ยังไม่มีรายการหลุม</h3>
              <p className='text-gray-600 mb-0 max-w-400px'>
                รายการหลุมจะปรากฏเมื่อมีการตอกเสาเข็มในโครงการนี้
              </p>
            </div>
          </div>
        ) : (
          <>
            <PlanSummaryCards data={tableRows} />

            <div className='px-9 pb-4'>
              <div className='d-flex flex-wrap gap-2'>
                {STATUS_FILTERS.map(f => (
                  <button
                    key={f.value}
                    type='button'
                    className={clsx('btn btn-sm', {
                      'btn-primary': statusFilter === f.value,
                      'btn-light': statusFilter !== f.value,
                    })}
                    onClick={() => setStatusFilter(f.value)}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className='card-body px-0 pt-0'>
              <ClientSideTable
                isGrey
                pagination
                columns={COLUMN_CONFIGS}
                data={filteredData}
                items_per_page={10}
              />
            </div>
          </>
        )}
      </div>

      <PreboreSettingModal
        isOpen={openPreboreModal}
        handleClose={handleClosePreboreModal}
        initialDepth={
          selectedPrebore.preboreDepth != null ? String(selectedPrebore.preboreDepth) : ''
        }
        initialDrillSize={
          selectedPrebore.preboreDrillSize != null ? String(selectedPrebore.preboreDrillSize) : ''
        }
        initialPileNo={selectedPrebore.pileNo ?? ''}
        initialGridLine={selectedPrebore.gridLine ?? ''}
        initialLocation={selectedPrebore.location ?? ''}
        isSaving={isSavingPrebore}
        onSave={handleSavePrebore}
      />
    </>
  )
}

export default HoleList
